import { faCopy } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { motion } from 'motion/react';
import QRCode from 'qrcode';
import { useEffect, useState } from 'react';

interface CryptoDepositScreenProps {
  isOpen: boolean;
  onClose: () => void;
  onBack: () => void;
}

export const CryptoDepositScreen = ({
  isOpen,
  onClose,
  onBack,
}: CryptoDepositScreenProps) => {
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string>('');
  const [copySuccess, setCopySuccess] = useState(false);

  // Example wallet address (this would normally come from your backend)
  const walletAddress = '0x4EC344FD7A4E2447F5768Cd8d9a85D30EXAMPLE';

  useEffect(() => {
    if (isOpen) {
      // Generate QR code with example text
      QRCode.toDataURL(walletAddress, {
        width: 200,
        margin: 0,
        color: {
          dark: '#FFFFFF',
          light: '#1a1a1a',
        },
      }).then(setQrCodeDataUrl);
    }
  }, [isOpen]);

  const handleCopyAddress = async () => {
    try {
      await navigator.clipboard.writeText(walletAddress);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy: ', err);
    }
  };

  return (
    <motion.div
      className="px-6 pb-8"
      initial={{ x: '100%' }}
      animate={{ x: isOpen ? 0 : '100%' }}
      exit={{ x: '100%' }}
      transition={{
        type: 'spring',
        damping: 30,
        stiffness: 300,
        duration: 0.4,
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <motion.button
          onClick={onBack}
          className="flex items-center gap-2 text-white hover:text-brand-green transition-colors"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path
              d="M15 18L9 12L15 6"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </motion.button>

        <h2 className="font-['Inter:Semi_Bold',_sans-serif] font-semibold text-[#ffffff] text-[24px]">
          Deposit Crypto
        </h2>

        <motion.button
          onClick={onClose}
          className="p-2 rounded-full hover:bg-gray-700 transition-colors"
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path
              d="M18 6L6 18M6 6L18 18"
              stroke="#FAFBFB"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </motion.button>
      </div>

      {/* Content */}
      <div className="flex flex-col items-center text-center">
        <div className="mb-4">
          <p className="font-['Inter:Medium',_sans-serif] font-medium text-[#ffffff] text-[16px]">
            Send USDC on Origin Protocol
          </p>
        </div>

        {/* QR Code */}
        <div className="mb-4">
          {qrCodeDataUrl && (
            <motion.div
              className="p-3 rounded-xl"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.3 }}
            >
              <img src={qrCodeDataUrl} alt="QR Code" className="w-32 h-32" />
            </motion.div>
          )}
        </div>

        {/* Wallet Address */}
        <div className="mb-4 w-full">
          <motion.div
            className="bg-[#2a2a2a] rounded-lg p-3 border"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.3 }}
          >
            <p className="font-['Inter:Medium',_sans-serif] font-medium text-[#bbbbbb] text-[14px] break-all">
              {walletAddress}
            </p>
          </motion.div>
        </div>

        {/* Copy Button */}
        <motion.button
          onClick={handleCopyAddress}
          className="w-full bg-[#2a2a2a] border border-white/30 text-white font-['Inter:Semi_Bold',_sans-serif] font-semibold py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.6, duration: 0.3 }}
        >
          <FontAwesomeIcon icon={faCopy} />
          {copySuccess ? 'Copied!' : 'Copy Address'}
        </motion.button>
      </div>
    </motion.div>
  );
};
